"use client";

import { useId, useState, type FormEvent } from "react";
import { api } from "@/lib/api";

/** Newsletter sign-up. Consent is given by subscribing: the wording under
 *  the field says so. The address is subscribed straight away and a
 *  thank-you email goes out (no confirmation link). */
export function NewsletterForm({ source = "website", tone = "light" }: { source?: string; tone?: "light" | "dark" }) {
  const id = useId();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);
  const dark = tone === "dark";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setState("sending");
    try {
      await api.newsletterSubscribe(email.trim(), true, source);
      setState("sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-up failed. Try again.");
      setState("idle");
    }
  }

  if (state === "sent") {
    return (
      <p role="status" className={`rounded-2xl px-4 py-3 text-[0.9rem] font-semibold ${dark ? "bg-emerald-400/15 text-emerald-100 ring-1 ring-emerald-300/30" : "bg-good-pale text-good"}`}>
        Thanks for subscribing! You're on the list. A thank-you note is on its way to your inbox.
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate={false}>
      <label htmlFor={`${id}-email`} className={dark ? "sr-only" : "mb-1.5 block text-[0.86rem] font-bold"}>
        {dark ? "Your email address" : "Get the Rankbist newsletter"}
      </label>
      {/* The field and the button share one pill, so it reads as a single control. */}
      <div
        className={`flex items-center gap-1.5 rounded-full p-1.5 transition-shadow focus-within:ring-4 ${
          dark ? "bg-white/10 ring-1 ring-white/20 focus-within:ring-violet/40" : "border border-line bg-white focus-within:border-violet focus-within:ring-violet/20"
        }`}
      >
        <span className={`hidden pl-3 sm:block ${dark ? "text-white/50" : "text-ink-softer"}`} aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="2" />
            <path d="M4 7.5l8 6 8-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <input
          id={`${id}-email`}
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`h-11 min-w-0 flex-1 bg-transparent px-3 text-[0.95rem] outline-none ${dark ? "text-white placeholder:text-white/45" : "text-ink placeholder:text-ink-softer"}`}
        />
        <button
          type="submit"
          disabled={state === "sending"}
          className={`h-11 flex-shrink-0 rounded-full px-6 text-[0.9rem] font-extrabold transition-transform hover:-translate-y-0.5 disabled:opacity-60 ${
            dark ? "bg-white text-violet-deep shadow-[0_12px_28px_-12px_rgba(255,255,255,0.7)]" : "bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep text-white"
          }`}
        >
          {state === "sending" ? "Signing up…" : "Subscribe"}
        </button>
      </div>
      <p className={`mt-3 text-[0.8rem] leading-snug ${dark ? "text-white/55" : "text-ink-soft"}`}>
        By subscribing you agree to receive the Rankbist newsletter by email. You can unsubscribe at any time from any issue.
      </p>
      {error && (
        <p role="alert" className={`mt-2 text-[0.82rem] font-semibold ${dark ? "text-amber-200" : "text-warn"}`}>
          {error}
        </p>
      )}
    </form>
  );
}
